import { ArrowUpRight, LoaderCircle, Search, X } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import clsx from "clsx";

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header">
    <div>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    {actions && <div className="page-actions">{actions}</div>}
  </header>;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "success" | "warning" | "danger" | "info" | "purple" }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function StatusBadge({ status }: { status: string }) {
  const tone = ["won", "active", "ready", "completed", "connected", "customer"].includes(status) ? "success"
    : ["urgent", "failed", "lost", "error"].includes(status) ? "danger"
      : ["proposal", "commit", "pending", "high", "qualified"].includes(status) ? "warning"
        : ["draft", "paused", "viewer"].includes(status) ? "purple" : "neutral";
  return <Badge tone={tone}>{status.replaceAll("_", " ")}</Badge>;
}

export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state">{icon}<h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Loading({ label = "Loading workspace" }: { label?: string }) {
  return <div className="loading"><LoaderCircle size={24} className="spin" /><span>{label}</span></div>;
}

export function SearchBox({ value, onChange, placeholder = "Search" }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search-box"><Search size={17} /><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />{value && <button onClick={() => onChange("")} aria-label="Clear search"><X size={15} /></button>}</label>;
}

export function Metric({ label, value, detail, icon, tone = "teal" }: { label: string; value: ReactNode; detail?: string; icon: ReactNode; tone?: string }) {
  return <article className="metric-card"><div className={`metric-icon tone-${tone}`}>{icon}</div><div className="metric-label">{label}</div><div className="metric-value">{value}</div>{detail && <div className="metric-detail">{detail} <ArrowUpRight size={13} /></div>}</article>;
}

export function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose]);
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()} role="presentation"><section className="modal" role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button className="icon-button" onClick={onClose}><X size={19} /></button></header>{children}</section></div>;
}

export function Button({ children, variant = "primary", className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger" }) {
  return <button className={clsx("button", `button-${variant}`, className)} {...props}>{children}</button>;
}
